/**
 * Shared setup screen shell used by every game.
 *
 * Renders the consistent outer layout (cream bg, back button, title/subtitle,
 * theme selector, difficulty selector, start button). Game-specific sections
 * (mode selectors, operation grids, previews, etc.) are passed as:
 *
 *   children  — rendered between the theme and difficulty sections
 *   preview   — rendered between the difficulty section and the start button
 *
 * Usage:
 *   <GameSetupScreen
 *     title="Memoria"   subtitle="..."   backLabel="← Inicio"
 *     startLabel="Empezar →"   onBack={...}   onStart={...}
 *     difficulties={[{ id: 'easy', label: 'Fácil', hint: '4 pares' }, ...]}
 *     selectedDifficulty={difficulty}   onDifficultyChange={setDifficulty}
 *     chooseDifficultyLabel="Elige la dificultad"
 *     availableThemes={...}   selectedTheme={...}   onThemeChange={...}
 *     classicEmoji="🃏"   classicLabel="Clásico"   chooseThemeLabel="Elige un tema"
 *     onGoToSubscription={...}
 *   >
 *     <ModeSelector ... />   ← game-specific
 *   </GameSetupScreen>
 *
 * Export `ss` for shared style tokens (optionLabel, modeCard, modeCardActive, …)
 * so game-specific card grids look identical to the built-in sections.
 */

import type { ReactNode } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import type { ThemeSummary } from '../api/client'
import { ss } from './gameSetupStyles'

export interface DifficultyOption {
  id:    string
  label: string
  hint:  string
}

interface GameSetupScreenProps {
  // Header
  title:     string
  subtitle:  string
  backLabel: string
  onBack:    () => void

  // Theme selector (omit to hide the whole section)
  availableThemes?:    ThemeSummary[]
  selectedTheme?:      { slug: string; id: number } | null
  onThemeChange?:      (t: ThemeSummary | null) => void
  classicEmoji?:       string
  classicLabel?:       string
  chooseThemeLabel?:   string
  onGoToSubscription?: () => void

  // Game-specific sections (mode pickers, op grids, etc.)
  children?: ReactNode

  // Difficulty selector
  difficulties:          DifficultyOption[]
  selectedDifficulty:    string
  onDifficultyChange:    (id: string) => void
  chooseDifficultyLabel: string

  // Preview (between difficulty + start)
  preview?: ReactNode

  // Start button
  startLabel:     string
  onStart:        () => void
  themeLoading?:  boolean
  startDisabled?: boolean
}

export default function GameSetupScreen({
  title, subtitle, backLabel, onBack,
  availableThemes, selectedTheme, onThemeChange,
  classicEmoji = '🃏', classicLabel, chooseThemeLabel,
  onGoToSubscription,
  children,
  difficulties, selectedDifficulty, onDifficultyChange, chooseDifficultyLabel,
  preview,
  startLabel, onStart, themeLoading = false, startDisabled = false,
}: GameSetupScreenProps) {
  const { language } = useTranslation()
  const hasThemes = availableThemes && availableThemes.length > 0

  return (
    <div style={ss.setupRoot}>
      <div style={ss.setupInner}>

        {/* Back button */}
        <button onClick={onBack} style={ss.backBtn} aria-label={backLabel}>
          {backLabel}
        </button>

        {/* Title */}
        <div className="fade-up">
          <h1 style={ss.setupTitle}>{title}</h1>
          <p style={ss.setupSubtitle}>{subtitle}</p>
        </div>

        {/* Theme selector */}
        {hasThemes && (
          <section className="fade-up" style={{ animationDelay: '0.05s' }}>
            <h2 style={ss.optionLabel}>{chooseThemeLabel}</h2>
            <div style={ss.themeRow}>
              {/* Classic option */}
              <button
                onClick={() => onThemeChange?.(null)}
                style={{ ...ss.themeBtn, ...(!selectedTheme ? ss.themeBtnActive : {}) }}
                aria-pressed={!selectedTheme}
              >
                <span style={ss.themeEmoji}>{classicEmoji}</span>
                <span style={ss.themeLabel}>{classicLabel}</span>
              </button>

              {/* Theme options */}
              {availableThemes!.map((theme) => {
                const isActive = selectedTheme?.slug === theme.slug
                const locked   = theme.locked
                return (
                  <button
                    key={theme.slug}
                    onClick={() => locked ? onGoToSubscription?.() : onThemeChange?.(theme)}
                    style={{ ...ss.themeBtn, ...(isActive ? ss.themeBtnActive : {}), ...(locked ? ss.themeBtnLocked : {}) }}
                    aria-pressed={isActive}
                    title={locked ? theme.requiredTier : undefined}
                  >
                    <div style={{ position: 'relative' as const, width: 48, height: 48, flexShrink: 0 }}>
                      {theme.coverUrl
                        ? <img src={theme.coverUrl} alt="" style={ss.themeCover} />
                        : <span style={ss.themeEmoji}>🖼️</span>
                      }
                      {locked && <span style={ss.lockBadge}>🔒</span>}
                    </div>
                    <span style={ss.themeLabel}>{language === 'es' ? theme.nameEs : theme.nameEn}</span>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* Game-specific sections (mode pickers, op grids, …) */}
        {children}

        {/* Difficulty selector */}
        <section className="fade-up" style={{ animationDelay: '0.2s' }}>
          <h2 style={ss.optionLabel}>{chooseDifficultyLabel}</h2>
          <div style={ss.diffRow}>
            {difficulties.map((d) => (
              <button
                key={d.id}
                onClick={() => onDifficultyChange(d.id)}
                style={{ ...ss.diffBtn, ...(selectedDifficulty === d.id ? ss.diffBtnActive : {}) }}
                aria-pressed={selectedDifficulty === d.id}
              >
                <span style={ss.diffLabel}>{d.label}</span>
                <span style={ss.diffHint}>{d.hint}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Preview slot */}
        {preview && (
          <div className="fade-up" style={{ animationDelay: '0.25s' }}>
            {preview}
          </div>
        )}

        {/* Start button */}
        <div className="fade-up" style={{ animationDelay: '0.3s' }}>
          <button
            onClick={onStart}
            disabled={themeLoading || startDisabled}
            style={{ ...ss.startBtn, ...((themeLoading || startDisabled) ? ss.startBtnDisabled : {}) }}
          >
            {themeLoading ? '…' : startLabel}
          </button>
        </div>

      </div>
    </div>
  )
}

